/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTheme } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import type { DailyReadingsSchema } from "@packages/sdk";
import type { ThemeType } from "@packages/ui/emotion";
import {
  Divider,
  fontFamilies,
  fontSizes,
  fontWeights,
  lineHeights,
  RecommendationDeeplink,
  spacing,
  stroke,
  Text,
} from "@packages/ui/emotion";
import {
  addMonths,
  endOfMonth,
  parse,
  startOfMonth,
  subMonths,
} from "date-fns";
import { useEffect, useState } from "react";

import { LoadingPage } from "../../../components";
import { useBibleHTML } from "../../../lib";
import { DailyReadingsNavButtons } from "../DailyReadingsNavButtons";

const marginsStyle: Interpolation = {
  marginBottom: spacing.m,
  marginTop: spacing.m,
};

type DailyReadingDisplayProps = {
  dailyReading: DailyReadingsSchema;
  date: `${number}-${number}-${number}`;
};

const DailyReadingCSS = (theme: ThemeType) => `
:root {
    --font-multi: 1;
}
.daily-readings {
    width: 95vw;
    max-width: ${spacing.maxReaderWidth};
    margin: auto;
    font-family: ${fontFamilies.serif};
    padding: ${spacing.s};
}
.daily-readings .header {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: ${spacing.xxxl} 0;
}
.daily-readings .header-divider {
    display: none;
}
.daily-readings .title {
    text-align: center;
    font-size: ${fontSizes.headlineS};
    line-height: ${lineHeights.headlineS};
    /* we intentionally do not use Headline font-weight here */
}
.daily-readings .reading-heading {
    margin: ${spacing.l} 0;
}
.daily-readings .reading-title {
    font-size: calc(var(--font-multi) * ${fontSizes.titleL});
    line-height: calc(var(--font-multi) * ${lineHeights.titleL});
    font-weight: ${fontWeights.titleL};
    font-family: ${fontFamilies.sansSerif};
}
.daily-readings .readings-container {
    margin-bottom: ${spacing.ml};
}
.daily-readings .reading-body p {
    font-size: calc(var(--font-multi) * ${fontSizes.bodyBible});
    line-height: calc(var(--font-multi) * ${lineHeights.bodyBible});
    font-weight: ${fontWeights.bodyBible};
    margin-block: ${spacing.m};
}
.daily-readings .reading-ref, .daily-readings .subtitle {
    font-size: calc(var(--font-multi) * ${fontSizes.titleM});
    line-height: calc(var(--font-multi) * ${lineHeights.titleM});
    font-family: ${fontFamilies.sansSerif};
}
.daily-readings .divider {
    width: ${spacing.full};
    height: ${spacing.none};
    border: ${stroke.light} solid ${theme.semanticColors.neutralsLowest};
    margin: ${spacing.m} 0;
}
`;

export const DailyReadingDisplay = ({
  dailyReading,
  date,
}: DailyReadingDisplayProps) => {
  const [html1, html2, html3] = useBibleHTML(dailyReading.html);
  const [dailyReadingCSS, setDailyReadingCSS] = useState<string>("");
  const [isMounted, setIsMounted] = useState(false);
  const t = useTranslations();
  const theme = useTheme() as ThemeType;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isMounted) {
      setDailyReadingCSS(DailyReadingCSS(theme));
    }
  }, [theme, isMounted]);

  if (!isMounted) {
    return <LoadingPage />;
  }

  return (
    <main className={"daily-readings"}>
      <style>{dailyReadingCSS}</style>
      <div dangerouslySetInnerHTML={{ __html: html1 }} />
      <RecommendationDeeplink {...dailyReading.top_content_deeplink} />
      <div dangerouslySetInnerHTML={{ __html: html2 }} />
      {!!dailyReading.bottom_content_deeplink ? (
        <>
          <Divider orientation={"horizontal"} css={marginsStyle} />
          <Text as={"h2"} type={"title"} size={"l"} css={marginsStyle}>
            {t("further_listening_title")}
          </Text>
          <RecommendationDeeplink
            {...dailyReading.bottom_content_deeplink}
            css={marginsStyle}
          />
        </>
      ) : null}
      <div dangerouslySetInnerHTML={{ __html: html3 }} />
      <DailyReadingsNavButtons
        currentDate={parse(date, "yyyy-MM-dd", new Date())}
        minDate={startOfMonth(subMonths(new Date(), 1))}
        maxDate={endOfMonth(addMonths(new Date(), 1))}
      />
    </main>
  );
};
